/* Turning a flat list of fields into the form people see.
 *
 * Frappe stores layout as *breaks* in a flat list: a Tab Break starts a tab,
 * a Section Break a section, a Column Break a column, and everything after a
 * break belongs to it until the next one. Drawing that literally gives the
 * endless single scroll that makes ERPNext's own forms so tiring, so the
 * breaks are turned into a tree first and the form renders the tree.
 */
import type { DocField } from './types'

export type Column = { fields: DocField[] }
export type Section = { label?: string; collapsible: boolean; collapseWhen?: string; showWhen?: string; columns: Column[] }
export type Tab = { label: string; sections: Section[] }

/** The field types this module consumes rather than rendering as controls.
 *  Named so the coverage test can check that a type marked "handled by
 *  layout.ts" really is, instead of taking the ledger's word for it. */
export const LAYOUT_HANDLED: readonly string[] = [
  'Tab Break', 'Section Break', 'Column Break', 'Heading',
]

export function buildLayout(fields: DocField[]): Tab[] {
  const tabs: Tab[] = []
  let tab: Tab | null = null
  let section: Section | null = null
  let column: Column | null = null

  const newTab = (label: string) => { tab = { label, sections: [] }; tabs.push(tab); section = null; column = null }
  const newSection = (f?: DocField) => {
    if (!tab) newTab('Details')
    section = {
      label: f?.label || undefined,
      collapsible: Boolean(f?.collapsible),
      collapseWhen: f?.collapsible_depends_on,
      showWhen: f?.depends_on,
      columns: [],
    }
    tab!.sections.push(section)
    column = null
  }
  const newColumn = () => { if (!section) newSection(); column = { fields: [] }; section!.columns.push(column) }

  for (const f of fields) {
    if (f.hidden) continue
    switch (f.fieldtype) {
      case 'Tab Break': newTab(f.label || 'More'); break
      case 'Section Break': newSection(f); break
      case 'Column Break': newColumn(); break
      case 'Heading':
        newSection({ ...f, collapsible: undefined })
        break
      default:
        if (!column) newColumn()
        column!.fields.push(f)
    }
  }

  // Breaks with nothing after them are common in ERPNext, and an empty
  // section with a heading is a heading for nothing.
  for (const t of tabs) {
    t.sections = t.sections
      .map((s) => ({ ...s, columns: s.columns.filter((c) => c.fields.length > 0) }))
      .filter((s) => s.columns.length > 0)
  }
  return tabs.filter((t) => t.sections.length > 0)
}

/** The field a Currency control should read its currency from. ERPNext names
 *  it in `options` (`currency`, or `Company:company:default_currency`). */
export function currencyFieldOf(field: DocField): string | null {
  const raw = field.options
  if (!raw) return null
  if (!raw.includes(':')) return raw
  const parts = raw.split(':')
  return parts[parts.length - 1] ?? null
}
